"use client";

/**
 * /agents — "How agents are organised" diagram.
 *
 * Pure CSS + inline SVG. One top node (Router) splits to three specialist
 * tracks (Plan, Execute, Reflect) and then converges back into the user-
 * facing surfaces. No graph library, no external assets.
 */

const SPECIALISTS = ["Plan", "Execute", "Reflect"];
const SURFACES = ["Research", "Code", "Chat", "News", "Browser"];

export function AgentFlowDiagram() {
  return (
    <div className="relative w-full overflow-hidden rounded-2xl border hairline-strong bg-background p-6 md:p-10">
      {/* Decorative grid */}
      <div aria-hidden className="absolute inset-0 grid-bg-dense opacity-[0.18] pointer-events-none" />

      <div className="relative">
        {/* ── Tier 1 — entrypoint ───────────────────────────────────────── */}
        <div className="flex justify-center">
          <DiagramNode label="Your prompt" tone="ghost" />
        </div>

        <Connector />

        {/* ── Tier 2 — router ───────────────────────────────────────────── */}
        <div className="flex justify-center">
          <DiagramNode label="Router" tone="solid" subtitle="Foundational" />
        </div>

        {/* ── Tier 2→3 — fan-out lines ──────────────────────────────────── */}
        <FanOut count={SPECIALISTS.length} />

        {/* ── Tier 3 — pipeline specialists ─────────────────────────────── */}
        <div className="grid grid-cols-3 gap-3 md:gap-6">
          {SPECIALISTS.map((s) => (
            <div key={s} className="flex justify-center">
              <DiagramNode label={s} tone="outline" />
            </div>
          ))}
        </div>

        <Connector />

        {/* ── Tier 4 — converge label ──────────────────────────────────── */}
        <div className="flex justify-center">
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-foreground/65">
            handed to the specialist surface
          </span>
        </div>

        <Connector />

        {/* ── Tier 5 — surfaces (the apps) ──────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3">
          {SURFACES.map((s) => (
            <div key={s} className="flex justify-center">
              <DiagramNode label={s} tone="outline" size="sm" />
            </div>
          ))}
        </div>

        <Connector />

        {/* ── Tier 6 — reflector + memory ──────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3 md:gap-6 max-w-md mx-auto">
          <div className="flex justify-center">
            <DiagramNode label="Reflector" tone="outline" size="sm" />
          </div>
          <div className="flex justify-center">
            <DiagramNode label="Memory" tone="outline" size="sm" />
          </div>
        </div>

        <Connector />

        <div className="flex justify-center">
          <DiagramNode label="Your answer" tone="ghost" />
        </div>
      </div>
    </div>
  );
}

/* ─── Internal building blocks ────────────────────────────────────────── */

function DiagramNode({
  label,
  subtitle,
  tone = "outline",
  size = "md",
}: {
  label: string;
  subtitle?: string;
  tone?: "solid" | "outline" | "ghost";
  size?: "sm" | "md";
}) {
  const pad   = size === "sm" ? "px-3 py-1.5" : "px-4 py-2";
  const text  = size === "sm" ? "text-xs"     : "text-sm";

  const toneCls =
    tone === "solid"
      ? "bg-foreground text-background border-foreground"
      : tone === "ghost"
      ? "bg-transparent text-foreground/65 border-foreground/20 border-dashed"
      : "bg-background text-foreground border-foreground/40";

  return (
    <span
      className={`inline-flex flex-col items-center justify-center rounded-full border ${toneCls} ${pad} ${text} font-medium whitespace-nowrap`}
    >
      <span>{label}</span>
      {subtitle && (
        <span className="font-mono text-[9px] tracking-[0.14em] uppercase opacity-65 mt-0.5">
          {subtitle}
        </span>
      )}
    </span>
  );
}

function Connector() {
  return (
    <div aria-hidden className="flex justify-center my-3 md:my-4">
      <span className="block h-6 w-px bg-foreground/35" />
    </div>
  );
}

function FanOut({ count }: { count: number }) {
  // Three angled lines drawn as SVG; widths track the grid above.
  return (
    <div aria-hidden className="relative h-8 md:h-10 my-1">
      <svg viewBox="0 0 300 40" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
        {Array.from({ length: count }).map((_, i) => {
          // Even spacing across viewBox 0..300
          const x = ((i + 0.5) / count) * 300;
          return (
            <line
              key={i}
              x1={150}
              y1={0}
              x2={x}
              y2={40}
              stroke="currentColor"
              strokeWidth={1}
              className="text-foreground/35"
            />
          );
        })}
      </svg>
    </div>
  );
}
